import { useCallback, useMemo } from "react";
import { useRouter } from "next/router";
import {
  MessageThreadSortType,
  SearchFilter,
  SearchFilterType,
} from "gql/graphql";
import { union } from "lodash";
import moment from "moment";
import { DATE_RANGE_TYPE } from "modules/utils/Constants";
import useQueryString, {
  FILTER_QUERY_STRING,
  SORT_QUERY_STRING,
} from "./use-query-string";
import { useMixpanel } from "modules/providers/mixpanel-provider";
import {
  MIXPANEL_EVENT_TYPE,
  PERSON_COUNT_TYPE,
} from "modules/providers/mixpanel-provider/mixpanel-provider";

const useSearchFilter = () => {
  const { query } = useRouter();
  const { push } = useRouter();
  const { qsSearchFilters, qsSortType } = useQueryString();
  const { track, incrementPersonProperty } = useMixpanel();

  const getUrl = useCallback(
    (
      path: string,
      sortType?: MessageThreadSortType,
      ...filters: SearchFilter[]
    ) => {
      const params = new URLSearchParams();
      if (sortType) {
        params.set("sortType", sortType);
      }

      if (filters.length > 0) {
        filters.forEach((filter) => {
          params.append(FILTER_QUERY_STRING, JSON.stringify(filter));
        });
      }

      const _query = params.toString();
      return _query ? `${path}?${_query}` : path;
    },
    [],
  );

  const getSearchFilter = useCallback(
    (type: SearchFilterType) =>
      qsSearchFilters.find((filter) => filter.type === type),
    [qsSearchFilters],
  );

  const trackSearch = useCallback(() => {
    incrementPersonProperty(PERSON_COUNT_TYPE.SEARCHES);
    const textFilter = getSearchFilter(SearchFilterType.Text);
    const tagsFilter = getSearchFilter(SearchFilterType.TagIdList);
    const userFilter = getSearchFilter(SearchFilterType.UserIdList);
    const distributionGroupsFilter = getSearchFilter(
      SearchFilterType.DistributionGroupIdList,
    );

    track(MIXPANEL_EVENT_TYPE.SEARCH, {
      text: textFilter ? textFilter.stringValue : undefined,
      tagCount:
        tagsFilter && tagsFilter.objectIdListValue
          ? tagsFilter.objectIdListValue.length
          : 0,
      distributionGroupCount:
        distributionGroupsFilter && distributionGroupsFilter.objectIdListValue
          ? distributionGroupsFilter.objectIdListValue.length
          : 0,
      userCount:
        userFilter && userFilter.objectIdListValue
          ? userFilter.objectIdListValue.length
          : 0,
    });
  }, [getSearchFilter, incrementPersonProperty, track]);

  const normalizeFilters = useCallback((_searchFilters: SearchFilter[]) => {
    const normalized = [..._searchFilters];
    normalized.sort((a, b) => a.type.localeCompare(b.type));
    normalized.forEach((searchFilter) => {
      if (
        searchFilter.objectIdListValue &&
        searchFilter.objectIdListValue.length > 0
      ) {
        searchFilter.objectIdListValue = [
          ...searchFilter.objectIdListValue,
        ].sort();
      }
      if (
        searchFilter.stringListValue &&
        searchFilter.stringListValue.length > 0
      ) {
        searchFilter.stringListValue = [...searchFilter.stringListValue].sort();
      }
    });
    return normalized;
  }, []);

  const serializeFilters = useCallback(
    (filters: SearchFilter[]): string | string[] | undefined => {
      if (filters.length === 0) return undefined;
      const serialized = normalizeFilters(filters).map((f) =>
        JSON.stringify(f),
      );
      return serialized.length === 1 ? serialized[0] : serialized;
    },
    [normalizeFilters],
  );

  const addOrReplaceFilters = useCallback(
    (_searchFilters: SearchFilter[], newPath?: string) => {
      const newSearchFilters = [...qsSearchFilters].filter(
        (searchFilter) =>
          !_searchFilters.some((filter) => filter.type === searchFilter.type),
      );
      newSearchFilters.push(..._searchFilters);

      const nextQuery = { ...query };

      delete nextQuery.filter;

      const filterValue = serializeFilters(newSearchFilters);
      if (filterValue) {
        nextQuery.filter = filterValue;
      }

      push({
        pathname: newPath,
        query: nextQuery,
      });

      trackSearch();
    },
    [qsSearchFilters, query, serializeFilters, push, trackSearch],
  );

  const setSearchFilters = useCallback(
    (newSearchFilters: SearchFilter[], newPath?: string) => {
      if (newSearchFilters.length > 0) {
        const nextQuery: Record<string, any> = {};
        const filterValue = serializeFilters(newSearchFilters);
        if (filterValue) {
          nextQuery.filter = filterValue;
        }
        push({
          pathname: newPath,
          query: nextQuery,
        });
        return;
      }
      push({
        pathname: newPath,
      });
      trackSearch();
    },
    [push, trackSearch, serializeFilters],
  );

  const removeFilterTypes = useCallback(
    (...type: SearchFilterType[]) => {
      const newSearchFilters = [...qsSearchFilters].filter(
        (searchFilter) => !type.includes(searchFilter.type),
      );

      const nextQuery = { ...query };

      delete nextQuery.filter;

      if (newSearchFilters.length > 0) {
        const filterValue = serializeFilters(newSearchFilters);
        if (filterValue) {
          nextQuery.filter = filterValue;
        }
      }

      push({
        query: nextQuery,
      });
    },
    [qsSearchFilters, query, push, serializeFilters],
  );

  const hasFilterType = useCallback(
    (type: SearchFilterType) =>
      qsSearchFilters.some((filter) => filter.type === type),
    [qsSearchFilters],
  );

  const hasFilterStringValue = useCallback(
    (type: SearchFilterType, value: string) =>
      qsSearchFilters.some(
        (filter) => filter.type === type && filter.stringValue === value,
      ),
    [qsSearchFilters],
  );

  const toggleFilter = useCallback(
    (type: SearchFilterType) => {
      if (hasFilterType(type)) {
        removeFilterTypes(type);
      } else {
        addOrReplaceFilters([{ type }]);
      }
    },
    [hasFilterType, removeFilterTypes, addOrReplaceFilters],
  );

  const removeString = useCallback(
    (type: SearchFilterType, value: string) => {
      const filter = getSearchFilter(type);
      if (filter?.stringListValue && filter.stringListValue.includes(value)) {
        const newStringListValue = filter.stringListValue.filter(
          (stringValue) => stringValue !== value,
        );
        if (newStringListValue.length === 0) {
          removeFilterTypes(type);
          return;
        }
        addOrReplaceFilters([
          {
            type,
            stringListValue: newStringListValue,
          },
        ]);
      }
    },
    [addOrReplaceFilters, getSearchFilter, removeFilterTypes],
  );

  const removeId = useCallback(
    (type: SearchFilterType, id: string) => {
      const filter = getSearchFilter(type);
      if (filter?.objectIdListValue && filter.objectIdListValue.includes(id)) {
        const newObjectIdListValue = filter.objectIdListValue.filter(
          (objectId) => objectId !== id,
        );
        if (newObjectIdListValue.length === 0) {
          removeFilterTypes(type);
          return;
        }
        addOrReplaceFilters([
          {
            type,
            objectIdListValue: newObjectIdListValue,
          },
        ]);
      }
    },
    [getSearchFilter, addOrReplaceFilters, removeFilterTypes],
  );

  const addId = useCallback(
    (type: SearchFilterType, id: string, newPath?: string) => {
      const filter = getSearchFilter(type);
      const newObjectIdListValue = [...(filter?.objectIdListValue ?? []), id];
      addOrReplaceFilters(
        [
          {
            type,
            objectIdListValue: newObjectIdListValue,
          },
        ],
        newPath,
      );
    },
    [getSearchFilter, addOrReplaceFilters],
  );

  const getCleanSearchTextList = (searchText: string) => {
    const splitWordsTextList = searchText
      .replace(/[\W_]+/g, " ")
      .split(" ")
      .filter((obj) => obj !== "");
    const wholeWordsTextList = searchText
      .split(" ")
      .filter((obj) => obj !== "");
    //whole words should go first in this array to ensure they are highlighted first
    return union(wholeWordsTextList, splitWordsTextList);
  };

  const cleanSearchTextList = useMemo(() => {
    const searchText = qsSearchFilters.find(
      (filter) => filter.type === SearchFilterType.Text,
    )?.stringValue;
    if (searchText) {
      return getCleanSearchTextList(searchText);
    }
  }, [qsSearchFilters]);

  const hasTextSearchFilter = useMemo(
    () => hasFilterType(SearchFilterType.Text),
    [hasFilterType],
  );

  const getStartDate = useCallback((dateRangeType: DATE_RANGE_TYPE) => {
    switch (dateRangeType) {
      case DATE_RANGE_TYPE.LAST_24_HOURS: {
        return moment().startOf("day").toISOString();
      }
      case DATE_RANGE_TYPE.LAST_7_DAYS: {
        return moment().startOf("week").toISOString();
      }
      case DATE_RANGE_TYPE.LAST_30_DAYS: {
        return moment().subtract(30, "day").startOf("day").toISOString();
      }
      case DATE_RANGE_TYPE.LAST_3_MONTHS: {
        return moment().subtract(3, "month").startOf("day").toISOString();
      }
      case DATE_RANGE_TYPE.THIS_YEAR: {
        return moment().startOf("year").toISOString();
      }
      default: {
        return undefined;
      }
    }
  }, []);

  const setSortType = useCallback(
    (sortType: MessageThreadSortType | undefined) => {
      const newQuery = { ...query };

      if (sortType === undefined) {
        delete newQuery[SORT_QUERY_STRING];
      } else {
        newQuery[SORT_QUERY_STRING] = sortType;
      }

      push({
        query: newQuery,
      });
    },
    [push, query],
  );
  const sortType = useMemo(
    (): MessageThreadSortType | undefined => qsSortType,
    [qsSortType],
  );

  return {
    getUrl,
    searchFilters: qsSearchFilters,
    getSearchFilter,
    addOrReplaceFilters,
    setSearchFilters,
    removeFilterTypes,
    hasFilterType,
    hasFilterStringValue,
    toggleFilter,
    removeId,
    removeString,
    addId,
    hasTextSearchFilter,
    cleanSearchTextList,
    getStartDate,
    setSortType,
    sortType,
  };
};

export default useSearchFilter;
